import { api } from '../../../services/api';

const PREFIX = 'widget-local-media:';

/** 页面持有照片与上传检查点；连线只传 opaque 引用，避免持久化本地源。 */
export class LocalMediaDraft {
  media = new Map();
  listeners = new Set();
  phase = 'idle';
  error = null;
  rejected = new Set();
  controller = new AbortController();

  /** React StrictMode 重挂 effect 时恢复空页面的生命周期。 */
  activate() { if (this.controller.signal.aborted) this.controller = new AbortController(); }

  /** 本地引用只能由当前页面解析。 */
  static isLocal(ref) { return typeof ref === 'string' && ref.startsWith(PREFIX); }

  get busy() { return this.phase !== 'idle'; }
  get signal() { return this.controller.signal; }

  /** 表单订阅真实处理阶段，以派生按钮状态。 */
  subscribe = (listener) => { this.listeners.add(listener); return () => this.listeners.delete(listener); };
  /** 通知所有表单，确保显示状态与宿主操作一致。 */
  notify() { this.listeners.forEach((listener) => listener()); }
  /** 仅由真实异步阶段推进状态，不使用延时猜测进度。 */
  setPhase(phase) { this.phase = phase; this.notify(); }

  /** 选图只创建预览；取消替换不会调用此方法，因此保留旧照片。 */
  stage(blob, previousRef) {
    this.signal.throwIfAborted();
    if (this.busy) return null;
    const ref = `${PREFIX}${crypto.randomUUID()}`;
    const preview = URL.createObjectURL(blob);
    this.remove(previousRef);
    this.media.forEach((media) => { media.uploaded = null; });
    this.media.set(ref, { blob, preview, uploaded: null });
    this.error = null;
    return ref;
  }

  /** 更换/移除照片使整批检查点失效，但不删除用户原件。 */
  remove(ref) {
    if (this.busy) return;
    const item = this.media.get(ref);
    if (!item) return;
    URL.revokeObjectURL(item.preview);
    this.media.delete(ref);
    this.media.forEach((media) => { media.uploaded = null; });
  }

  /** 失效引用返回空源，不能把 opaque 引用交给浏览器发网络请求。 */
  preview(ref) { return this.media.get(ref)?.preview || ''; }

  /** 先验证整个输入快照，再按角色顺序上传；固定远程参考图原位保留。 */
  async prepare(payload) {
    if (this.busy) return null;
    this.signal.throwIfAborted();
    const images = Array.isArray(payload.images) ? [...payload.images] : [];
    const roles = Array.isArray(payload.image_roles) ? [...payload.image_roles] : [];
    const local = images.filter(LocalMediaDraft.isLocal);
    this.error = null;
    this.setPhase('preparing');
    try {
      local.filter((ref) => !this.media.has(ref)).forEach((ref) => this.rejected.add(ref));
      if (!local.length || images.some((ref) => typeof ref !== 'string' || !ref.trim())
        || roles.some((role) => typeof role !== 'string' || !role.trim())
        || roles.length !== images.length || new Set(roles).size !== roles.length
        || (payload.required_media_count != null && Number(payload.required_media_count) !== images.length)
        || local.some((ref) => this.rejected.has(ref))) {
        throw new Error('Please select all required photos again.');
      }
      // 浏览器已持有 Blob，无需再转 base64；安全校验仍由公共上传入口执行。
      this.setPhase('uploading');
      for (const ref of local) {
        this.signal.throwIfAborted();
        const item = this.media.get(ref);
        if (!item.uploaded) {
          const ext = item.blob.type.includes('png') ? 'png' : item.blob.type.includes('webp') ? 'webp' : 'jpg';
          item.uploaded = await api.uploadBlob(item.blob, ext, {
            signal: this.signal,
            notifyComplete: true,
          });
          this.signal.throwIfAborted();
          if (!item.uploaded) throw new Error('Upload did not return a photo.');
        }
      }
      this.setPhase('dispatching');
      return { ...payload, images: images.map((ref) => this.media.get(ref)?.uploaded || ref), image_roles: roles };
    } catch (error) {
      if (!this.signal.aborted) this.fail('Unable to prepare your photos. Please try again.');
      throw error;
    }
  }

  /** 派发失败也保留已成功上传的检查点，下一次提交无需重复上传。 */
  finish() { if (!this.signal.aborted) this.setPhase('idle'); }
  /** 用户可读错误和已选预览一起保留，允许补选或重试。 */
  fail(message) { this.error = message; this.finish(); }

  /** 页面退出取消在途上传，释放本页面独占的预览 URL。 */
  dispose() {
    this.controller.abort();
    this.media.forEach((item) => URL.revokeObjectURL(item.preview));
    this.media.clear();
    this.phase = 'idle';
  }
}
